import clsx from 'clsx';
import {
  forwardRef,
  useMemo,
  useRef,
  type ElementType,
  type ReactElement,
  type ReactNode,
  Children,
} from 'react';
import { useIntl } from 'react-intl';
import { isElement } from 'react-is';

import { MenuIcon } from '@dynatrace/strato-icons';

import { Logo } from './Logo.js';
import * as styles from './Navigation.sty.js';
import { Button } from '../../buttons/button/Button.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { Menu } from '../../navigation/menu/Menu.js';
import { Flex } from '../flex/Flex.js';

/**
 * @public
 */
export interface AppHeaderNavigationProps
  extends StylingProps,
    DataTestId,
    WithChildren {}

function getSlots(children: ReactNode) {
  const linksProps: AppHeaderNavigationItemProps<ElementType>[] = [];
  let logo: ReactNode;

  for (const child of Children.toArray(children)) {
    if (!isElement(child)) {
      continue;
    }

    if (isSlot(child, NavigationItem)) {
      const key = child.key ?? crypto.randomUUID();
      const propsWithKey = Object.assign({}, child.props, { key });
      linksProps.push(propsWithKey);
    } else if (isSlot(child, Logo)) {
      logo = child;
    }
  }

  return { linksProps, logo };
}

function useCollapsingNav() {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(containerRef);
  const contentSize = useContainerSize(contentRef);

  return {
    isOverflowing: contentSize.width > containerSize.width,
    containerRef,
    contentRef,
  };
}

/**
 * Container for AppHeader navigation items
 * @public
 */
export const Navigation = forwardRef<HTMLDivElement, AppHeaderNavigationProps>(
  (props, ref) => {
    const {
      'data-testid': dataTestId,
      style,
      className,
      children,
      ...rest
    } = props;

    const slots = useMemo(() => getSlots(children), [children]);
    const collapsingNav = useCollapsingNav();
    const intl = useIntl();

    return (
      <>
        <Menu>
          <Menu.Trigger>
            <Button
              aria-label={intl.formatMessage({
                defaultMessage: 'Open app navigation',
                id: 'WCZAUtmjwNCm5iwa',
                description:
                  'Label for the menu trigger to open app-header navigation.',
              })}
              style={{
                display: collapsingNav.isOverflowing ? undefined : 'none',
              }}
            >
              <Button.Prefix>
                <MenuIcon />
              </Button.Prefix>
            </Button>
          </Menu.Trigger>
          <Menu.Content>
            {slots.linksProps.map((linkProps) => {
              const {
                isSelected,
                disabled,
                className: navItemClassName,
                ...rest
              } = linkProps;
              return (
                <Menu.Link
                  {...rest}
                  key={linkProps.key}
                  className={clsx(
                    styles.menuLink,
                    {
                      [styles.isSelected]: isSelected,
                      [styles.isDisabled]: disabled,
                    },
                    navItemClassName,
                  )}
                  disabled={disabled}
                />
              );
            })}
          </Menu.Content>
        </Menu>
        <Flex
          {...rest}
          ref={ref}
          gap={4}
          alignItems="center"
          flexShrink={1}
          minWidth={0}
          style={style}
          className={className}
          data-testid={dataTestId}
        >
          {slots.logo || <Logo />}
          <Flex
            ref={collapsingNav.containerRef}
            flexGrow={1}
            minWidth={0}
            hidden={collapsingNav.isOverflowing}
            style={{
              visibility: collapsingNav.isOverflowing ? 'hidden' : undefined,
              flexBasis: collapsingNav.isOverflowing ? 0 : undefined,
            }}
          >
            <Flex
              as="nav"
              gap={4}
              className={styles.inlineNav}
              ref={collapsingNav.contentRef}
              aria-label={intl.formatMessage({
                defaultMessage: 'App navigation',
                id: 'oA42meteX3Ww5Zo5',
              })}
            >
              {slots.linksProps.map((linkProps) => {
                const {
                  isSelected,
                  disabled,
                  as = 'a',
                  className: navItemClassName,
                  ...rest
                } = linkProps;

                return (
                  <Button
                    {...rest}
                    key={linkProps.key}
                    as={as}
                    className={clsx(
                      styles.button,
                      {
                        [styles.isSelected]: isSelected,
                        [styles.isDisabled]: disabled,
                      },
                      navItemClassName,
                    )}
                    disabled={disabled}
                  />
                );
              })}
            </Flex>
          </Flex>
        </Flex>
      </>
    );
  },
);
(Navigation as typeof Navigation & { displayName: string }).displayName =
  'AppHeader.Navigation';

/**
 * @public
 */
export interface AppHeaderNavigationItemOwnProps
  extends WithChildren,
    StylingProps,
    DataTestId {
  href?: string;
  disabled?: boolean;
  isSelected?: boolean;
}

/**
 * @public
 */
export type AppHeaderNavigationItemProps<E extends ElementType> =
  PolymorphicComponentProps<E, AppHeaderNavigationItemOwnProps>;

/**
 * Used to configure desktop and mobile navigation items
 * @public
 */
export const NavigationItem: <E extends ElementType = 'a'>(
  props: AppHeaderNavigationItemProps<E>,
) => ReactElement | null = forwardRef(
  <E extends ElementType>(
    props: AppHeaderNavigationItemProps<E>,
    forwardedRef: typeof props.ref,
  ) => <div ref={forwardedRef} {...props} />,
);
(
  NavigationItem as typeof NavigationItem & { displayName: string }
).displayName = 'AppHeader.NavigationItem';
